import { useState } from 'react';
import { notify } from 'src/utils/notification';
import { verifyEmailForReset, verifySMSForReset } from 'src/api/forgot-password';
import {
  ForgotPasswordMethod,
  IVerifyInfoData,
  IParsedOTPData,
  IVerifyEmailParams,
  IVerifySMSParams,
  VerifyParams,
  IApiResponse,
} from 'src/shared/interface/forgot-password';
import {
  APP_CODE,
  DEFAULT_MAX_VERIFY_ATTEMPTS,
  DEFAULT_VALUE,
  ERROR_MESSAGES,
  MILLISECONDS_MULTIPLIER,
  SUCCESS_CODE,
} from 'src/shared/constants';
import { ERROR_CODE } from 'src/shared/enum/errors_code.enum';

interface IUseVerifyStepProps {
  onSuccess: (username: string, identifier: string, otpData: IParsedOTPData) => void;
}

const isEmailParams = (params: VerifyParams): params is IVerifyEmailParams => {
  return 'email' in params && 'username' in params;
};

const isSMSParams = (params: VerifyParams): params is IVerifySMSParams => {
  return 'phoneNumber' in params;
};

const parseOTPData = (data: IVerifyInfoData): IParsedOTPData => {
  const now = Date.now();
  const isResendBlocked = data.currentCounter >= data.maxCounter || data.isBlock;
  const hasVerifyLock = data.lockVerifyInSecond > DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS;

  return {
    expiresAt: now + data.otpExpireIn * MILLISECONDS_MULTIPLIER,
    resendCooldownUntil: now + data.lockGetOtpInSecond * MILLISECONDS_MULTIPLIER,
    isResendLocked: isResendBlocked,
    resendLockUntil: isResendBlocked
      ? now + data.countdownTimeInSecond * MILLISECONDS_MULTIPLIER
      : null,
    isVerifyLocked: hasVerifyLock,
    verifyLockUntil: hasVerifyLock ? now + data.lockVerifyInSecond * MILLISECONDS_MULTIPLIER : null,
    resendCount: data.currentCounter,
    maxResendCount: data.maxCounter,
    verifyAttempts: DEFAULT_VALUE.DEFAULT_VALUE_VERIFY_ATTEMPTS,
    maxVerifyAttempts: DEFAULT_MAX_VERIFY_ATTEMPTS,

    countdownTimeInSecond: data.countdownTimeInSecond,
    isBlock: data.isBlock,
  };
};

const validateParams = (method: ForgotPasswordMethod, params: VerifyParams): boolean => {
  if (method === ForgotPasswordMethod.EMAIL && isEmailParams(params)) {
    if (!params.username || !params.email) {
      notify.error('Vui lòng nhập đầy đủ thông tin');
      return false;
    }
  } else if (method === ForgotPasswordMethod.SMS && isSMSParams(params)) {
    if (!params.phoneNumber) {
      notify.error('Vui lòng nhập số điện thoại');
      return false;
    }
  }
  return true;
};

const extractCredentials = (
  method: ForgotPasswordMethod,
  params: VerifyParams,
): { username: string; identifier: string } => {
  if (method === ForgotPasswordMethod.EMAIL && isEmailParams(params)) {
    return {
      username: params.username,
      identifier: params.email,
    };
  }

  if (isSMSParams(params)) {
    return {
      username: params.phoneNumber,
      identifier: params.phoneNumber,
    };
  }

  throw new Error('Invalid params type');
};

const isSuccessResponse = (res: IApiResponse): boolean => {
  return res?.code === SUCCESS_CODE;
};

const isOTP410Or412Response = (res: IApiResponse): boolean => {
  return (
    (res?.data as IVerifyInfoData)?.code === ERROR_CODE.OTP_410 ||
    (res?.data as IVerifyInfoData)?.code === ERROR_CODE.OTP_412 ||
    (res?.data as IVerifyInfoData)?.code === ERROR_CODE.OTP_411 ||
    (res?.data as IVerifyInfoData)?.code === ERROR_CODE.OTP_409
  );
};

export const useVerifyStep = ({ onSuccess }: IUseVerifyStepProps) => {
  const [loading, setLoading] = useState(false);

  const handleVerify = async (method: ForgotPasswordMethod, params: VerifyParams) => {
    if (!validateParams(method, params)) return;

    setLoading(true);
    try {
      const res =
        method === ForgotPasswordMethod.EMAIL && isEmailParams(params)
          ? await verifyEmailForReset({ ...params, appCode: APP_CODE })
          : await verifySMSForReset({
              ...(params as IVerifySMSParams),
              appCode: APP_CODE,
            });
      if (isSuccessResponse(res) || isOTP410Or412Response(res)) {
        const { username, identifier } = extractCredentials(method, params);
        const otpData = parseOTPData(res.data);

        if (isOTP410Or412Response(res)) {
          notify.warning(res.data.message || 'Vui lòng thử lại sau');
        } else {
          notify.success('Mã xác thực đã được gửi');
        }
        onSuccess(username, identifier, otpData);
      } else {
        throw new Error(res.message || ERROR_MESSAGES.DEFAULT);
      }
    } catch (error: any) {
      const errorResponse = error?.response?.data?.data;
      if (
        errorResponse?.code === ERROR_CODE.OTP_410 ||
        errorResponse?.code === ERROR_CODE.OTP_412 ||
        errorResponse?.code === ERROR_CODE.OTP_411 ||
        errorResponse?.code === ERROR_CODE.OTP_409
      ) {
        const { username, identifier } = extractCredentials(method, params);
        const otpData = parseOTPData(errorResponse);

        onSuccess(username, identifier, otpData);
        return;
      }
      throw error;
    } finally {
      setLoading(false);
    }
  };
  return { loading, handleVerify };
};
